import { useEffect } from 'react';
import { Modal } from '../../components/modal/modal';
import { useCallback, useContext, useState } from 'react';
import { Button } from '../../components/form/button';
import { toast } from '../../util/toasts';
import { AsyncCreatableSelect } from '../../components/select-form/select-form';
import { debounce } from 'lodash';
import { Typography } from '../../components/typography/typography';
import { components } from 'react-select';
import Icon from '../../components/icons/icon';
import classNames from 'classnames';
import { TaxJurisdictionModal } from './tax-jurisdiction-modal';
import { Field } from '../../components/field/field';
import { TaxCode, TaxJurisdiction } from '../../clients/quipli-service/tax/tax';
import { useTaxApiV1 } from '../../clients/quipli-service/tax/tax-api-v1';
import { QboTaxCodeSelect } from '../qbo/qbo-tax-code-select';
import { TenantContext } from '../../context/tenant-context';

export const TaxRateModal = ({
  isOpen,
  close,
  taxCode,
}: {
  isOpen: boolean;
  close: (qboChanged: boolean) => void;
  taxCode?: TaxCode;
}) => {
  const { configuration } = useContext(TenantContext);
  const { createTaxCode, updateTaxCode, searchTaxJurisdictions } = useTaxApiV1();

  const defaultTaxRateRequest: {
    name: string;
    jurisdictionIds: string[];
    qboTaxCodeId?: string;
    source: 'manual';
  } = {
    name: '',
    jurisdictionIds: [],
    qboTaxCodeId: '',
    source: 'manual',
  };
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [taxRateRequest, setTaxRateRequest] = useState<{
    name: string;
    jurisdictionIds: string[];
    qboTaxCodeId?: string;
    source: 'manual';
  }>(defaultTaxRateRequest);
  const [isTaxJurisdictionModalOpen, setIsTaxJurisdictionModalOpen] = useState<boolean>(false);

  const [jurisdictions, setJurisdictions] = useState<TaxJurisdiction[]>([]);

  useEffect(() => {
    if (taxCode) {
      setTaxRateRequest({
        name: taxCode.name,
        jurisdictionIds: taxCode.jurisdictions.map((tj) => tj.id),
        qboTaxCodeId: taxCode.qboTaxCodeId,
        source: 'manual',
      });
    }
  }, [taxCode]);

  async function doSave() {
    if (!taxCode) {
      try {
        setIsSaving(true);
        await createTaxCode(taxRateRequest);
        toast('Tax code created successfully');
        close(false);
        clearForm();
      } catch (e: any) {
        toast(`Failed to save tax code`, 'error');
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsSaving(true);
      try {
        await updateTaxCode({ ...taxRateRequest, id: taxCode.id });
        toast('Tax code updated successfully');
        close(taxRateRequest.qboTaxCodeId !== taxCode.qboTaxCodeId);
        clearForm();
      } catch (e: any) {
        toast(`Failed to save tax code`, 'error');
      } finally {
        setIsSaving(false);
      }
    }
  }

  function doClose() {
    close(false);
    clearForm();
  }

  function clearForm() {
    setTaxRateRequest(defaultTaxRateRequest);
  }

  const search = useCallback(
    debounce((keyword: string, callback) => {
      searchTaxJurisdictions({ taxJurisdictionName: keyword }).then((res) => {
        if (keyword === '') {
          setJurisdictions(res);
        }
        callback(res);
      });
    }, 300),
    [],
  );

  let qboEnabled = false;
  // @hipaa-exclude-start
  if (configuration?.qboEnabled) {
    qboEnabled = true;
  }
  // @hipaa-exclude-end
  return (
    <Modal
      className={'overflow-visible'}
      title={taxCode ? 'Update Tax Code' : 'Create Tax Code'}
      isOpen={isOpen}
      close={doClose}
      actions={
        <>
          <Button className="button button-secondary" disabled={isSaving} onClick={doClose}>
            Cancel
          </Button>
          <Button
            className="button button-yellow"
            disabled={isSaving || (taxCode && taxRateRequest.jurisdictionIds.length == 0)}
            onClick={doSave}
          >
            {taxCode ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="flex flex-column gap-2">
        <div>
          {
            <>
              <div>
                <Field
                  type="text"
                  label="Name"
                  value={taxRateRequest.name}
                  onChange={(name: string) => setTaxRateRequest({ ...taxRateRequest, name })}
                />
              </div>
              <div>
                <Typography type={'text-sm'}>Tax Codes</Typography>
                <AsyncCreatableSelect<TaxJurisdiction, true>
                  isMulti={true}
                  cacheOptions
                  defaultOptions
                  isDisabled={false}
                  loadOptions={search}
                  value={jurisdictions.filter((j) => taxRateRequest.jurisdictionIds.includes(j.id))}
                  isOptionSelected={(option) => taxRateRequest.jurisdictionIds.includes(option.id)}
                  onClickCreatableOption={() => setIsTaxJurisdictionModalOpen(true)}
                  getOptionLabel={(op) => op.name}
                  getOptionValue={(op) => `${op.id}`}
                  menuPortalTarget={document.body}
                  onChange={(value) => {
                    setTaxRateRequest((prev) => {
                      return {
                        ...prev,
                        jurisdictionIds: value.map((v) => v.id),
                      };
                    });
                  }}
                  loadingMessage={() => 'Loading jurisdictions...'}
                  noOptionsMessage={() => 'No jurisdictions found'}
                  components={{
                    Placeholder: (props) => (
                      <components.Placeholder {...props}>
                        <Typography type="text-sm">Add Jurisdictions</Typography>
                      </components.Placeholder>
                    ),
                    ValueContainer: (props) => (
                      <div className="flex items-center gap-[6px] pl-[10px]">
                        <Icon icon="Search" size="medium" stroke="var(--grey-500)" />
                        <components.ValueContainer {...props} />
                      </div>
                    ),
                    Option: (props) => {
                      //@ts-expect-error __isNew__
                      const isCreateOption = props.data.__isNew__ === true;

                      return (
                        <components.Option {...props}>
                          <div
                            className={classNames('flex items-center gap-[6px]', {
                              'justify-between': !isCreateOption,
                            })}
                          >
                            {isCreateOption ? (
                              <>
                                <div className="quipli-v2 flex items-center">
                                  <Icon icon="Plus" stroke="var(--quipli-black-800)" />
                                </div>
                                <Typography type="text-md" weight="medium" className="text-grey-900">
                                  Add new jurisdiction
                                </Typography>
                              </>
                            ) : (
                              <>
                                <div className="flex flex-col gap-[4px]">
                                  <Typography type="text-md" weight="medium" className="text-grey-900">
                                    {props.children}
                                  </Typography>
                                  <div>
                                    <Typography type="text-sm" className="text-grey-500">
                                      {props.data.percentage}%
                                    </Typography>
                                  </div>
                                </div>
                                {props.isSelected && <Icon icon="Check" stroke="var(--quipli-black-800)" />}
                              </>
                            )}
                          </div>
                        </components.Option>
                      );
                    },
                  }}
                />
              </div>
            </>
          }
          {qboEnabled && (
            <div>
              <label>QuickBooks Tax Code</label>
              <QboTaxCodeSelect
                taxCodeId={taxRateRequest.qboTaxCodeId}
                setTaxCodeId={(qboTaxCodeId) => {
                  setTaxRateRequest((prev) => {
                    return { ...prev, qboTaxCodeId };
                  });
                }}
              />
            </div>
          )}
        </div>
      </div>
      <TaxJurisdictionModal
        isOpen={isTaxJurisdictionModalOpen}
        close={() => {
          setIsTaxJurisdictionModalOpen(false);
        }}
      ></TaxJurisdictionModal>
    </Modal>
  );
};
